import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useRecoilValue } from 'recoil';
import { Button, Dialog, Input } from '../../../components/ui';
import { createUserFormDataAtom } from '../../../Services/_states/index.js';
import { validatePassword } from '../utils/userValidation';

export default function UserChangePasswordDialog({ open, onClose, onChangePassword }) {
  const userFormData = useRecoilValue(createUserFormDataAtom);
  const [formData, setFormData] = useState({ password: '', confirm_password: '' });
  const [validationErrors, setValidationErrors] = useState({
    password: '',
    confirm_password: '',
  });
  const [passwordRules, setPasswordRules] = useState({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
    noCommonWords: true,
    noUsernameSubstring: false,
  });

  const handleClose = () => {
    setFormData({ password: '', confirm_password: '' });
    setValidationErrors({ password: '', confirm_password: '' });
    onClose?.();
  };

  const handleSubmit = () => {
    const errors = {};
    const password = formData?.password;
    const { isValid, rules } = validatePassword(password, userFormData?.username || '');
    if (!isValid) {
      errors.password = 'Password does not meet all reuirements';
    }
    if (password !== formData?.confirm_password) {
      errors.confirm_password = 'Passwords do not match.';
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length === 0) {
      onChangePassword?.(password);
      handleClose();
    }
  };

  const handlePasswordChange = (value) => {
    setFormData((prev) => ({ ...prev, password: value }));
    const { isValid, rules } = validatePassword(value, userFormData?.username || '');
    setPasswordRules(rules);
    setValidationErrors((prev) => ({
      ...prev,
      password: isValid ? '' : 'Password does not meet all requirements',
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Change Password"
      actions={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Change Password
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Input
          label="New Password"
          required
          type="password"
          value={formData.password}
          onChange={(event) => handlePasswordChange(event.target.value)}
          error={!!validationErrors.password}
          errorMessage={validationErrors.password}
        />

        <Typography variant="subtitle2">Password must meet the following requirements:</Typography>
        <Box>
          {[
            ['minLength', 'At least 12 characters'],
            ['uppercase', 'At least one uppercase letter (A-Z)'],
            ['lowercase', 'At least one lowercase letter (a-z)'],
            ['number', 'At least one number (0-9)'],
            ['specialChar', 'At least one special character'],
            ['noCommonWords', "Must not include common words (e.g., 'password', 'admin')"],
            ['noUsernameSubstring', 'Must not contain your username'],
          ].map(([key, text]) => (
            <Typography
              key={key}
              variant="caption"
              display="flex"
              alignItems="center"
              color={passwordRules?.[key] ? 'success.main' : 'error.main'}
            >
              {passwordRules?.[key] ? (
                <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
              ) : (
                <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
              )}
              {text}
            </Typography>
          ))}
        </Box>

        <Input
          label="Confirm Password"
          required
          type="password"
          value={formData.confirm_password}
          onChange={(event) => {
            setFormData((prev) => ({ ...prev, confirm_password: event.target.value }));
            setValidationErrors((prev) => ({ ...prev, confirm_password: '' }));
          }}
          error={!!validationErrors.confirm_password}
          errorMessage={validationErrors.confirm_password}
        />
      </Box>
    </Dialog>
  );
}

UserChangePasswordDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onChangePassword: PropTypes.func,
};
